type WakeLockSentinelLike = {
  readonly released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
  removeEventListener(type: 'release', listener: () => void): void;
};

type WakeLockApi = {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
};

function getWakeLockApi(): WakeLockApi | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const wakeLock = (navigator as Navigator & { wakeLock?: WakeLockApi })
    .wakeLock;
  return typeof wakeLock?.request === 'function' ? wakeLock : undefined;
}

function isDocumentVisible(): boolean {
  return (
    typeof document !== 'undefined' && document.visibilityState === 'visible'
  );
}

/**
 * Screen Wake Lock helper for Autoplay.
 * Autoplay ON → request → keep awake → re-acquire on visibility → Autoplay OFF → release.
 */
export class ScreenWakeLockManager {
  private sentinel: WakeLockSentinelLike | null = null;
  private wanted = false;
  private requestInFlight: Promise<void> | null = null;
  private visibilityBound = false;

  private readonly handleVisibilityChange = (): void => {
    if (!this.wanted || !isDocumentVisible()) return;
    void this.requestLock();
  };

  private readonly handleSentinelRelease = (): void => {
    this.detachSentinel();
    if (this.wanted && isDocumentVisible()) {
      void this.requestLock();
    }
  };

  /** Enable while Autoplay is active; disable when it stops. */
  setEnabled(enabled: boolean): void {
    if (enabled === this.wanted) {
      if (enabled) void this.requestLock();
      return;
    }

    this.wanted = enabled;
    if (enabled) {
      this.bindVisibility();
      void this.requestLock();
      return;
    }

    this.unbindVisibility();
    void this.releaseLock();
  }

  dispose(): void {
    this.setEnabled(false);
  }

  private bindVisibility(): void {
    if (this.visibilityBound || typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.visibilityBound = true;
  }

  private unbindVisibility(): void {
    if (!this.visibilityBound || typeof document === 'undefined') return;
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    this.visibilityBound = false;
  }

  private async requestLock(): Promise<void> {
    if (this.requestInFlight) {
      await this.requestInFlight;
    }

    if (!this.wanted) return;
    if (this.sentinel && !this.sentinel.released) return;
    if (!isDocumentVisible()) return;

    const api = getWakeLockApi();
    if (!api) return;

    this.requestInFlight = (async () => {
      try {
        const sentinel = await api.request('screen');
        if (!this.wanted || (this.sentinel && !this.sentinel.released)) {
          await sentinel.release().catch(() => undefined);
          return;
        }
        this.sentinel = sentinel;
        sentinel.addEventListener('release', this.handleSentinelRelease);
      } catch (error) {
        console.warn(
          '[ScreenWakeLock] request failed; Autoplay continues',
          error
        );
      }
    })().finally(() => {
      this.requestInFlight = null;
    });

    await this.requestInFlight;
  }

  private async releaseLock(): Promise<void> {
    const sentinel = this.sentinel;
    this.detachSentinel();
    if (!sentinel || sentinel.released) return;
    try {
      await sentinel.release();
    } catch (error) {
      console.warn('[ScreenWakeLock] release failed', error);
    }
  }

  private detachSentinel(): void {
    if (!this.sentinel) return;
    this.sentinel.removeEventListener('release', this.handleSentinelRelease);
    this.sentinel = null;
  }
}
